/**
 * §P.5 fase 2 · Editar la rúbrica de un examen.
 *
 * ## El `PUT` **sustituye la rúbrica entera**
 *
 * Como las disciplinas del club, y con la misma consecuencia: lo que no se manda se
 * pierde. Por eso la tabla arranca con lo que hay —incluidas las plantillas— y se
 * manda completa. Un editor que mandara «lo que he tocado» borraría en silencio los
 * criterios que no tocó.
 *
 * ## Cuelga del **grado**, no del examen
 *
 * «Poomsae de amarillo se puntúa igual en diciembre y en junio». Así que cada fila
 * lleva su grado, y los grados que se ofrecen son **los grados destino de los
 * inscritos**: son los únicos sobre los que este examen puede escribir.
 *
 * Y lo que se guarda no es solo de este examen: **sustituye a las plantillas globales
 * de esos grados** para todo el club, de aquí en adelante. La pantalla lo dice antes
 * de guardar, porque es un cambio más grande de lo que parece desde una ficha de
 * examen.
 *
 * ## Se valida antes de mandar, y no por comodidad
 *
 * Con un `PUT` que sustituye, un `422` a mitad de la lista tira las sesenta filas.
 * Descubrir el error al pulsar «guardar» con la tabla llena es perder el trabajo, así
 * que cada fila dice lo que le falta mientras se escribe.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import {
  aCriterio,
  faltaEnLaFila,
  filaDesde,
  filaVacia,
  type CriterioDeExamen,
  type FilaDeRubrica,
} from '@/club/grados/tiposDeExamen';

export function EditorDeRubrica({
  club,
  examenId,
  criterios,
  grados,
  alCerrar,
}: {
  club: string;
  examenId: string;
  criterios: readonly CriterioDeExamen[];
  /** Los grados destino de los inscritos: los únicos sobre los que se escribe. */
  grados: readonly { id: string; nombre: string }[];
  alCerrar: () => void;
}) {
  const cache = useQueryClient();
  /*
   * Arranca con **todo lo que hay**, plantillas incluidas. Es un `PUT` que sustituye:
   * empezar con lo propio dejaría fuera las plantillas sin que nadie lo decidiera.
   */
  const [filas, setFilas] = useState<readonly FilaDeRubrica[]>(() =>
    criterios.map(filaDesde)
  );
  const [problema, setProblema] = useState<Problema | null>(null);

  const cambiar = (clave: string, campo: keyof FilaDeRubrica, valor: string) =>
    setFilas((f) =>
      f.map((x) => (x.clave === clave ? { ...x, [campo]: valor } : x))
    );

  const guardar = useMutation({
    mutationFn: async () => {
      const convertidas = filas.map(aCriterio);
      if (convertidas.some((c) => c === null)) {
        throw new ErrorDeApi({
          title: 'Hay filas sin terminar',
          status: 400,
          detail:
            'Cada criterio necesita código, nombre, un máximo y un peso mayores que cero. Las filas con problema lo dicen debajo.',
        });
      }

      const r = await api.v1
        .clubs({ club })
        ['exam-events']({ id: examenId })
        .criteria.put({
          criteria: convertidas.map((c, i) => ({ ...c!, sort_order: i })),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['criterios', club, examenId] });
      alCerrar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const conPlantilla = criterios.some((c) => !c.is_own);

  return (
    <form
      className="formulario"
      onSubmit={(e) => {
        e.preventDefault();
        setProblema(null);
        guardar.mutate();
      }}
    >
      <h2 className="seccion">Editar la rúbrica</h2>

      {/* Lo que de verdad va a pasar al guardar, antes de guardar. */}
      <div className="aviso" role="status">
        <p>
          <strong>Esto sustituye la rúbrica entera</strong> de los grados de
          abajo: lo que quites de la tabla deja de existir. Empieza con lo que
          ya había para que no se pierda nada sin querer.
        </p>
        {conPlantilla && (
          <p>
            Algunos criterios vienen de <strong>las plantillas de Kiap</strong>.
            Al guardar pasan a ser del club y sustituyen a la plantilla en esos
            grados, para este examen y para los siguientes. Las plantillas
            globales no se tocan: las comparten doscientos clubes.
          </p>
        )}
      </div>

      {filas.length === 0 ? (
        <p className="tenue">
          La tabla está vacía. Añade criterios, o cierra sin guardar para
          dejarla como estaba.
        </p>
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th>Grado</th>
                <th>Código</th>
                <th>Criterio</th>
                <th>Apartado</th>
                <th className="num">Máximo</th>
                <th className="num">Peso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => {
                const falta = faltaEnLaFila(f);
                return (
                  <tr key={f.clave}>
                    <td>
                      <select
                        value={f.belt_rank_id ?? ''}
                        onChange={(e) =>
                          setFilas((x) =>
                            x.map((y) =>
                              y.clave === f.clave
                                ? {
                                    ...y,
                                    belt_rank_id:
                                      e.target.value === ''
                                        ? null
                                        : e.target.value,
                                  }
                                : y
                            )
                          )
                        }
                      >
                        <option value="">Todos</option>
                        {grados.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.nombre}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        value={f.code}
                        maxLength={40}
                        placeholder="poomsae"
                        onChange={(e) =>
                          cambiar(f.clave, 'code', e.target.value)
                        }
                      />
                      {/* Lo que falta, en la fila y mientras se escribe. */}
                      {falta && <span className="tenue">{falta}</span>}
                    </td>
                    <td>
                      <input
                        value={f.label}
                        maxLength={120}
                        placeholder="Poomsae Taegeuk Il Jang"
                        onChange={(e) =>
                          cambiar(f.clave, 'label', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        value={f.category}
                        maxLength={40}
                        placeholder="Técnica"
                        onChange={(e) =>
                          cambiar(f.clave, 'category', e.target.value)
                        }
                      />
                    </td>
                    <td className="num">
                      <input
                        inputMode="decimal"
                        value={f.max_score}
                        onChange={(e) =>
                          cambiar(f.clave, 'max_score', e.target.value)
                        }
                      />
                    </td>
                    <td className="num">
                      <input
                        inputMode="decimal"
                        value={f.weight}
                        onChange={(e) =>
                          cambiar(f.clave, 'weight', e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() =>
                          setFilas((x) => x.filter((y) => y.clave !== f.clave))
                        }
                      >
                        Quitar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="acciones">
        <button
          type="button"
          className="btn-plano"
          onClick={() =>
            setFilas((f) => [...f, filaVacia(grados[0]?.id ?? null)])
          }
        >
          Añadir un criterio
        </button>
      </div>

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        <button type="submit" disabled={guardar.isPending}>
          {guardar.isPending ? 'Guardando…' : 'Guardar la rúbrica'}
        </button>
        <button
          type="button"
          className="btn-plano"
          disabled={guardar.isPending}
          onClick={alCerrar}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
